import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { News } from '../../models/news/news.model';
import { NewsPeriodicity } from '../../models/news/news-periodicity.model';
import { NewsPeriodicityType } from '../../enums/news/news-periodicity-type.enum';
import { NewsPredefined } from '../../models/news/news-predefined.model';
import { UserCurrencyType } from '../../enums/users/user-currency-type.enum';
import { ApiNewsService } from '../../services/news/api/api-news.service';
import { AlertsService } from '../../services/alerts.service';
import { ModalsService } from '../../services/modals.service';
import { DateService } from '../../services/date.service';
import { NewsAddModalComponent } from './modals/news-add-modal/news-add-modal.component';
import { NewsEditModalComponent } from './modals/news-edit-modal/news-edit-modal.component';
import { NewsRewardsModalComponent } from './modals/news-rewards-modal/news-rewards-modal.component';
import { NewsPredefinedAddModalComponent } from './modals/news-predefined-add-modal/news-predefined-add-modal.component';
import { NewsPredefinedEditModalComponent } from './modals/news-predefined-edit-modal/news-predefined-edit-modal.component';
import { NewsPredefinedTranslationsModalComponent } from './modals/news-predefined-translations-modal/news-predefined-translations-modal.component';
import { ConfigurationsBlockComponent } from '../configurations/configurations-block/configurations-block.component';
import { ConfigurationDef } from '../configurations/configurations-block/configuration-def.model';

@Component({
  templateUrl: 'news.component.html',
  standalone: true,
  imports: [
    CommonModule, TooltipModule, TabsModule,
    NewsAddModalComponent, NewsEditModalComponent, NewsRewardsModalComponent,
    NewsPredefinedAddModalComponent, NewsPredefinedEditModalComponent, NewsPredefinedTranslationsModalComponent,
    ConfigurationsBlockComponent
  ]
})
export class NewsComponent implements OnInit {

  protected readonly NewsPeriodicityType = NewsPeriodicityType;

  readonly newsConfigurations: ConfigurationDef[] = [
    { name: 'news_user_targeted_unpublish_timeout', label: 'User Targeted Unpublish Timeout', type: 'number' },
  ];

  // --- News tab ---
  @ViewChild('addModal') addModal!: NewsAddModalComponent;
  @ViewChild('editModal') editModal!: NewsEditModalComponent;
  @ViewChild('newsRewardsModal') newsRewardsModal!: NewsRewardsModalComponent;

  newsList: News[] = [];
  newsIsLoading: boolean = false;
  newsTotalResults: number = 0;
  private static newsPageNumber: number = 1;
  readonly resultsPerPage: number = 25;

  get newsPageNumber(): number { return NewsComponent.newsPageNumber; }
  get newsTotalPages(): number { return Math.ceil(this.newsTotalResults / this.resultsPerPage); }

  // --- Predefined tab ---
  @ViewChild('predefinedAddModal') predefinedAddModal!: NewsPredefinedAddModalComponent;
  @ViewChild('predefinedEditModal') predefinedEditModal!: NewsPredefinedEditModalComponent;
  @ViewChild('predefinedTranslationsModal') predefinedTranslationsModal!: NewsPredefinedTranslationsModalComponent;

  predefinedList: NewsPredefined[] = [];
  predefinedIsLoading: boolean = false;
  predefinedTotalResults: number = 0;
  private static predefinedPageNumber: number = 1;

  get predefinedPageNumber(): number { return NewsComponent.predefinedPageNumber; }
  get predefinedTotalPages(): number { return Math.ceil(this.predefinedTotalResults / this.resultsPerPage); }

  constructor(
    public dateService: DateService,
    private apiNewsService: ApiNewsService,
    private alertsService: AlertsService,
    private modalsService: ModalsService
  ) {}

  ngOnInit(): void {
    this.refreshNews();
  }

  getPageNumbers(current: number, total: number): (number | null)[] {
    const visibleSet = new Set<number>();
    for (let i = 1; i <= Math.min(2, total); i++) visibleSet.add(i);
    for (let i = Math.max(total - 1, 1); i <= total; i++) visibleSet.add(i);
    for (let i = Math.max(1, current - 2); i <= Math.min(total, current + 2); i++) visibleSet.add(i);
    const sorted = Array.from(visibleSet).sort((a, b) => a - b);
    const result: (number | null)[] = [];
    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push(null);
      result.push(sorted[i]);
    }
    return result;
  }

  // --- News tab ---
  onSelectNewsTab(): void { this.refreshNews(); }

  newsGoToPage(page: number): void { NewsComponent.newsPageNumber = page; this.refreshNews(); }
  onClickNewsPaginationPrevious(): void { NewsComponent.newsPageNumber--; this.refreshNews(); }
  onClickNewsPaginationNext(): void { NewsComponent.newsPageNumber++; this.refreshNews(); }

  getNewsStatus(news: News): 'waiting' | 'in_progress' | 'past' | 'disabled' {
    if (!news.isActive) return 'disabled';
    const now = Math.floor(Date.now() / 1000);
    if (now < news.startTimestamp) return 'waiting';
    if (now > news.endTimestamp) return 'past';
    if (!news.periodicity) return 'in_progress';
    return this.isPeriodicityActive(news.periodicity) ? 'in_progress' : 'waiting';
  }

  getPeriodicityLabel(news: News): string {
    const p: any = news.periodicity;
    if (!p) return '';
    switch (p.type) {
      case NewsPeriodicityType.Daily:
        return `Daily ${p.time_start}–${p.time_end}`;
      case NewsPeriodicityType.Weekly: {
        const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
        const active = days.filter((_, i) => p.days_mask !== null && (p.days_mask & (1 << i)) !== 0);
        return `Weekly [${active.join(',')}] ${p.time_start}–${p.time_end}`;
      }
      case NewsPeriodicityType.Monthly:
        return `Monthly ${p.day_start}–${p.day_end} ${p.time_start}–${p.time_end}`;
      default:
        return '';
    }
  }

  getCurrencyTypeName(type: number): string { return UserCurrencyType[type] ?? String(type); }

  private isPeriodicityActive(p: NewsPeriodicity): boolean {
    const raw: any = p;
    const now = new Date();
    const hh = now.getHours().toString().padStart(2, '0');
    const mm = now.getMinutes().toString().padStart(2, '0');
    const currentTime = `${hh}:${mm}`;
    const timeStart = raw.time_start ?? p.timeStart;
    const timeEnd = raw.time_end ?? p.timeEnd;
    if (currentTime < timeStart || currentTime > timeEnd) return false;
    if (p.type === NewsPeriodicityType.Daily) return true;
    if (p.type === NewsPeriodicityType.Weekly) {
      const daysMask = raw.days_mask ?? p.daysMask;
      const dow = now.getDay();
      const bit = dow === 0 ? 6 : dow - 1;
      return daysMask !== null && (daysMask & (1 << bit)) !== 0;
    }
    if (p.type === NewsPeriodicityType.Monthly) {
      const dayStart = raw.day_start ?? p.dayStart;
      const dayEnd = raw.day_end ?? p.dayEnd;
      const dom = now.getDate();
      return dayStart !== null && dayEnd !== null && dom >= dayStart && dom <= dayEnd;
    }
    return false;
  }

  onClickAdd(): void {
    this.addModal.show(() => this.refreshNews());
  }

  onClickEdit(news: News): void {
    this.editModal.show(news.newsId, news, () => this.refreshNews());
  }

  onClickRewards(news: News): void {
    this.newsRewardsModal.show(news.newsId, news.name, news.rewards, () => {
      this.apiNewsService.getNews(this.newsPageNumber, this.resultsPerPage).then(
        response => {
          this.newsList = response.results;
          this.newsTotalResults = response.resultsTotal;
          const fresh = response.results.find(n => n.newsId === news.newsId);
          if (fresh) { this.newsRewardsModal.rewards = fresh.rewards; }
        }, error => {
          this.alertsService.displayAlertError('Error loading news : ' + error);
        }
      );
    });
  }

  onClickToggleActive(news: News): void {
    const newState = !news.isActive;
    this.apiNewsService.changeNewsActiveState(news.newsId, newState).then(
      () => {
        this.alertsService.displayAlertSuccess(newState ? 'News activated !' : 'News deactivated !');
        this.refreshNews();
      }, error => {
        this.alertsService.displayAlertError('Error changing active state : ' + error);
      }
    );
  }

  onClickDelete(news: News): void {
    this.modalsService.showConfirmModal(() => {
      this.apiNewsService.deleteNews(news.newsId).then(
        () => {
          this.alertsService.displayAlertSuccess('News deleted !');
          this.refreshNews();
        }, error => {
          this.alertsService.displayAlertError('Error deleting news : ' + error);
        }
      );
    });
  }

  // --- Predefined tab ---
  onSelectPredefinedTab(): void { this.refreshPredefined(); }

  predefinedGoToPage(page: number): void { NewsComponent.predefinedPageNumber = page; this.refreshPredefined(); }
  onClickPredefinedPaginationPrevious(): void { NewsComponent.predefinedPageNumber--; this.refreshPredefined(); }
  onClickPredefinedPaginationNext(): void { NewsComponent.predefinedPageNumber++; this.refreshPredefined(); }

  onClickAddPredefined(): void {
    this.predefinedAddModal.show(() => this.refreshPredefined());
  }

  onClickEditPredefined(item: NewsPredefined): void {
    this.predefinedEditModal.show(item, () => this.refreshPredefined());
  }

  onClickPredefinedTranslations(item: NewsPredefined): void {
    this.predefinedTranslationsModal.show(item.newsPredefinedId, item.name, item.translations, () => {
      this.apiNewsService.getNewsPredefined(this.predefinedPageNumber, this.resultsPerPage).then(
        response => {
          this.predefinedList = response.results;
          this.predefinedTotalResults = response.resultsTotal;
          const fresh = response.results.find(n => n.newsPredefinedId === item.newsPredefinedId);
          if (fresh) { this.predefinedTranslationsModal.translations = fresh.translations; }
        }, error => {
          this.alertsService.displayAlertError('Error loading predefined news : ' + error);
        }
      );
    });
  }

  onClickDeletePredefined(item: NewsPredefined): void {
    this.modalsService.showConfirmModal(() => {
      this.apiNewsService.deleteNewsPredefined(item.newsPredefinedId).then(
        () => {
          this.alertsService.displayAlertSuccess('Predefined news deleted !');
          this.refreshPredefined();
        }, error => {
          this.alertsService.displayAlertError('Error deleting predefined news : ' + error);
        }
      );
    });
  }

  private refreshNews(): void {
    this.newsIsLoading = true;
    this.apiNewsService.getNews(this.newsPageNumber, this.resultsPerPage).then(
      response => {
        this.newsIsLoading = false;
        this.newsList = response.results;
        this.newsTotalResults = response.resultsTotal;
        if (this.newsPageNumber > this.newsTotalPages && this.newsTotalPages > 0) {
          NewsComponent.newsPageNumber = 1;
          this.refreshNews();
        }
      }, error => {
        this.newsIsLoading = false;
        this.alertsService.displayAlertError('Error loading news : ' + error);
      }
    );
  }

  private refreshPredefined(): void {
    this.predefinedIsLoading = true;
    this.apiNewsService.getNewsPredefined(this.predefinedPageNumber, this.resultsPerPage).then(
      response => {
        this.predefinedIsLoading = false;
        this.predefinedList = response.results;
        this.predefinedTotalResults = response.resultsTotal;
        if (this.predefinedPageNumber > this.predefinedTotalPages && this.predefinedTotalPages > 0) {
          NewsComponent.predefinedPageNumber = 1;
          this.refreshPredefined();
        }
      }, error => {
        this.predefinedIsLoading = false;
        this.alertsService.displayAlertError('Error loading predefined news : ' + error);
      }
    );
  }
}
